import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  CircularProgress,
  Grid,
  Stack,
  Typography,
  Button,
  useTheme,
  Avatar,
  Card,
  CardContent,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  LinearProgress,
  Alert,
  AlertTitle,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import PersonIcon from "@mui/icons-material/Person";
import SelfImprovementIcon from "@mui/icons-material/SelfImprovement";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingFlatIcon from "@mui/icons-material/TrendingFlat";
import FavoriteIcon from "@mui/icons-material/Favorite";
import BedtimeIcon from "@mui/icons-material/Bedtime";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import BoltIcon from "@mui/icons-material/Bolt";
import AirIcon from "@mui/icons-material/Air";
import WaterDropIcon from "@mui/icons-material/WaterDrop";
import WhatshotIcon from "@mui/icons-material/Whatshot";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import ThermostatIcon from "@mui/icons-material/Thermostat";
import GpsFixedIcon from "@mui/icons-material/GpsFixed";
import AdsClickIcon from "@mui/icons-material/AdsClick";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import dayjs from "dayjs";

import PeakieWebLogo from "../../../../../assets/Peakie AI Web Logo.png";
import {
  StatCard,
  LineChart,
  RadarChart,
  HorizontalBarChart,
  CircularProgressIndicator,
} from "../../../../../shared/components/charts";
import { DashboardCard } from "../../../../../shared/components/DashboardCard";
import {
  FilterDrawer,
  FilterState,
  getAppliedFilterCount,
  DurationSelector,
  DateRange,
  DashboardCardSkeleton,
} from "../../../../../shared/components";
import { UserSelectSingle } from "../../../../../shared/components/Filters/components";
import {
  useInsightsChatbotConfig,
  useStandardInsights,
} from "../../../insights";
import type { User } from "../../../../../shared/components/Filters/api/types";
import {
  useClientDashboardSettings,
  usePersistedDateRange,
  usePersistedFilters,
} from "../../../../../shared/hooks";
import { useClientScope } from "../../../../../shared/contexts";
import {
  useUserProgressStats,
  useUserProgressTrends,
  useWellbeingDistribution,
} from "../hooks/useUserProgress";
import {
  buildFilterParams,
  transformTrendsData,
  calculateGranularity,
  roundToDecimal,
} from "../../shared/utils";
import {formatPercentValue} from "../../../../../shared/utils";

export const UserProgress: React.FC = () => {
  const theme = useTheme();
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const { filters, setFilters } = usePersistedFilters();
  const activeFiltersCount = getAppliedFilterCount(filters);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const { dateRange, setDateRange } = usePersistedDateRange();
  const [viewMode, setViewMode] = useState("user-progress");
  const { effectiveClientId } = useClientScope();
  const { data: dashboardSettings } = useClientDashboardSettings();
  const isAiEnabled =
    dashboardSettings?.data?.find(
      (setting) => setting.dashboard_key === "user_progress"
    )?.ai_enabled ?? true;
  const { setConfig: setChatbotConfig } = useInsightsChatbotConfig();
  const [biometricTrendView, setBiometricTrendView] = useState("heart-rate");
  const [sleepTrendView, setSleepTrendView] = useState("sleep-stages");
  const [recoveryTrendView, setRecoveryTrendView] = useState("recovery-score");

  const handleApplyFilters = (filters: FilterState) => {
    setFilters(filters);
  };

  const handleDateRangeChange = (range: DateRange) => {
    setDateRange(range);
  };

  const handleViewModeChange = (
    _event: React.MouseEvent<HTMLElement>,
    newValue: string | null
  ) => {
    if (newValue !== null) {
      setViewMode(newValue);
    }
  };

  useEffect(() => {
    if (!isAiEnabled && viewMode === "ai-powered-wellness") {
      setViewMode("user-progress");
    }
  }, [isAiEnabled, viewMode]);

  const userData = {
    name: selectedUser?.name ?? "Select a user",
    role:
      selectedUser?.role && selectedUser?.department
        ? `${selectedUser.role} - ${selectedUser.department}`
        : selectedUser?.role ?? selectedUser?.department ?? "",
    streak: 28, // Hardcoded for now as it's not in stats
    memberSince: "Jan 2024", // Hardcoded for now
    overallWellnessScore: 82, // Placeholder, updated from insights if available
    wellnessScoreChange: "+05%", // Placeholder
  };

  const insightsUserId = selectedUserId;

  const filterParams = useMemo(() => {
    const baseParams = buildFilterParams(filters, dateRange, effectiveClientId);
    if (selectedUserId) {
      return {
        ...baseParams,
        user_ids: [selectedUserId],
      };
    }
    return baseParams;
  }, [filters, dateRange, selectedUserId, effectiveClientId]);

  const granularity = useMemo(
    () => calculateGranularity(dateRange),
    [dateRange]
  );

  // --- API Hooks for User Progress Tab ---
  const statsQuery = useUserProgressStats(filterParams);
  const trendsQuery = useUserProgressTrends({
    ...filterParams,
    granularity,
  });
  const distributionQuery = useWellbeingDistribution(filterParams);

  // --- API Hooks for AI-Powered Wellness Tab ---
  const standardInsightsParams = useMemo(() => {
    const start = dateRange[0] ?? dayjs().startOf("month");
    const end = dateRange[1] ?? dayjs().endOf("month");
    return {
      scope_type: "user" as const,
      scope_id: insightsUserId ?? undefined,
      start_date: start.format("YYYY-MM-DD"),
      end_date: end.format("YYYY-MM-DD"),
      client_id: effectiveClientId || undefined,
    };
  }, [dateRange, insightsUserId, effectiveClientId]);

  const {
    data: standardInsightsData,
    isLoading: standardInsightsLoading,
    error: standardInsightsError,
  } = useStandardInsights(standardInsightsParams, {
    enabled: !!insightsUserId && isAiEnabled,
  });

  const standardInsights = standardInsightsData?.data;
  const headerWellnessScore =
    standardInsights?.overall_wellness_score.value ??
    userData.overallWellnessScore;

  const overallScoreSummarySection = useMemo(() => {
    const sections =
      standardInsights?.overall_wellness_score.calculation_summary_sections;
    if (!sections?.length) return null;
    return (
      sections.find((s) =>
        (s.title ?? "").toLowerCase().includes("overall score summary")
      ) ?? sections[0]
    );
  }, [standardInsights?.overall_wellness_score.calculation_summary_sections]);

  const getSummarySection = useMemo(() => {
    const sections =
      standardInsights?.overall_wellness_score.calculation_summary_sections ??
      [];
    return (titleIncludes: string) => {
      const needle = titleIncludes.toLowerCase();
      return (
        sections.find((s) => (s.title ?? "").toLowerCase().includes(needle)) ??
        null
      );
    };
  }, [standardInsights?.overall_wellness_score.calculation_summary_sections]);

  const keyHighlightsSection = useMemo(
    () => getSummarySection("key highlights"),
    [getSummarySection]
  );
  const recommendationsSection = useMemo(
    () => getSummarySection("recommendations"),
    [getSummarySection]
  );
  const areasToWatchSection = useMemo(
    () => getSummarySection("areas to watch"),
    [getSummarySection]
  );

  useEffect(() => {
    if (!insightsUserId) return;
    setChatbotConfig({
      title: "Peakie AI",
      initialPrompt: "What stands out this week and what should we do next?",
      buildRequest: (question) => ({
        scope: { type: "user", id: insightsUserId },
        question,
        start_date: standardInsightsParams.start_date,
        end_date: standardInsightsParams.end_date,
        client_id: standardInsightsParams.client_id,
        k: 8,
      }),
    });
  }, [
    insightsUserId,
    setChatbotConfig,
    standardInsightsParams.end_date,
    standardInsightsParams.start_date,
    standardInsightsParams.client_id,
  ]);

  // --- Data Transformation for User Progress Tab ---

  const trendsData = useMemo(() => {
    return transformTrendsData(trendsQuery.data, [
      "day_strain",
      "avg_heart_rate",
      "max_heart_rate",
      "respiratory_rate",
      "spo2",
      "sleep_efficiency",
      "sleep_debt",
      "sleep_stress",
      "hrv",
      "resting_heart_rate",
      "recovery_score",
      "sleep_stage_awake",
      "sleep_stage_light",
      "sleep_stage_rem",
      "sleep_stage_deep",
    ]);
  }, [trendsQuery.data]);

  const statCards = useMemo(() => {
    if (!statsQuery.data?.data) return [];
    const stats = statsQuery.data.data;
    return [
      {
        metric: stats.recovery_score,
        icon: FavoriteIcon,
      },
      {
        metric: stats.sleep_performance,
        icon: BedtimeIcon,
      },
      {
        metric: stats.hrv,
        icon: MonitorHeartIcon,
      },
      {
        metric: stats.day_strain,
        icon: BoltIcon,
      },
      {
        metric: stats.respiratory_rate,
        icon: AirIcon,
      },
      {
        metric: stats.spo2,
        icon: WaterDropIcon,
      },
      {
        metric: stats.calories_burn,
        icon: WhatshotIcon,
      },
    ].filter((card) => card.metric);
  }, [statsQuery.data]);

  const wellbeingDimensionsData = useMemo(() => {
    if (!distributionQuery.data?.data?.distribution) return [];
    // The API might return distributions. If it's a radar chart, we expect values for specific categories.
    // Assuming the API returns items with labels like "Recovery", "Sleep Quality", etc.
    return distributionQuery.data.data.distribution.map((item) => ({
      metric: item.label,
      currentScore: item.value,
      optimalTarget: 100, // Or whatever target is appropriate, maybe from API if available
    }));
  }, [distributionQuery.data]);

  const isLoading =
    statsQuery.isLoading ||
    trendsQuery.isLoading ||
    distributionQuery.isLoading;

  const hasError =
    statsQuery.isError || trendsQuery.isError || distributionQuery.isError;

  const renderBiometricTrendChart = () => {
    if (biometricTrendView === "heart-rate") {
      return (
        <LineChart
          data={trendsData}
          xAxisKey="label"
          series={[
            {
              dataKey: "avg_heart_rate",
              label: "Avg HR",
              color: theme.palette.info.main,
              area: true,
            },
            {
              dataKey: "resting_heart_rate",
              label: "Resting HR",
              color: theme.palette.success.main,
              area: true,
            },
            {
              dataKey: "max_heart_rate",
              label: "Max HR",
              color: theme.palette.error.main,
              dashed: true,
            },
          ]}
          height={300}
          yAxisMin={0}
        />
      );
    }

    if (biometricTrendView === "heart-rate-variability") {
      return (
        <LineChart
          data={trendsData}
          xAxisKey="label"
          series={[
            {
              dataKey: "hrv",
              label: "Heart Rate Variability",
              color: theme.palette.success.main,
              area: true,
            },
          ]}
          height={300}
          yAxisMin={0}
        />
      );
    }

    if (biometricTrendView === "resting-hr") {
      return (
        <LineChart
          data={trendsData}
          xAxisKey="label"
          series={[
            {
              dataKey: "resting_heart_rate",
              label: "Resting HR",
              color: theme.palette.warning.main,
              area: true,
            },
          ]}
          height={300}
          yAxisMin={0}
        />
      );
    }

    if (biometricTrendView === "respiratory-rate") {
      return (
        <LineChart
          data={trendsData}
          xAxisKey="label"
          series={[
            {
              dataKey: "respiratory_rate",
              label: "Respiratory Rate",
              color: theme.palette.warning.main,
              area: true,
            },
          ]}
          height={300}
          yAxisMin={0}
          yAxisMax={25}
        />
      );
    }

    return (
      <LineChart
        data={trendsData}
        xAxisKey="label"
        series={[
          {
            dataKey: "spo2",
            label: "Blood Oxygen (SpO2)",
            color: theme.palette.success.main,
            area: true,
          },
        ]}
        height={300}
        yAxisMin={0}
        yAxisMax={100}
      />
    );
  };

  const renderSleepTrendChart = () => {
    if (sleepTrendView === "sleep-stages") {
      return (
        <LineChart
          data={trendsData}
          xAxisKey="label"
          series={[
            {
              dataKey: "sleep_stage_deep",
              label: "Slow Wave (Deep)",
              color: theme.palette.success.main,
              area: true,
              stack: "sleep",
            },
            {
              dataKey: "sleep_stage_rem",
              label: "REM",
              color: theme.palette.info.dark,
              area: true,
              stack: "sleep",
            },
            {
              dataKey: "sleep_stage_light",
              label: "Light Sleep",
              color: theme.palette.info.light,
              area: true,
              stack: "sleep",
            },
            {
              dataKey: "sleep_stage_awake",
              label: "Awake",
              color: theme.palette.error.main,
              area: true,
              stack: "sleep",
            },
          ]}
          height={300}
          yAxisMin={0}
          yAxisMax={10} // Assuming sleep stages are in hours or a normalized metric
        />
      );
    }

    // Mapping other sleep trends
    const metricKey =
      sleepTrendView === "efficiency"
        ? "sleep_efficiency"
        : sleepTrendView === "sleep-debt"
        ? "sleep_debt"
        : "sleep_stress";

    const metricLabel =
      sleepTrendView === "efficiency"
        ? "Sleep Efficiency"
        : sleepTrendView === "sleep-debt"
        ? "Sleep Debt"
        : "Sleep Stress";

    const metricColor =
      sleepTrendView === "sleep-stress"
        ? theme.palette.error.main
        : theme.palette.success.main;

    return (
      <LineChart
        data={trendsData}
        xAxisKey="label"
        series={[
          {
            dataKey: metricKey,
            label: metricLabel,
            color: metricColor,
            area: true,
          },
        ]}
        height={300}
        yAxisMin={0}
        yAxisMax={100}
      />
    );
  };

  const renderRecoveryTrendChart = () => {
    if (recoveryTrendView === "recovery-score") {
      return (
        <LineChart
          data={trendsData}
          xAxisKey="label"
          series={[
            {
              dataKey: "recovery_score",
              label: "Recovery Score",
              color: theme.palette.success.main,
              area: true,
            },
          ]}
          height={300}
          yAxisMin={0}
          yAxisMax={100}
        />
      );
    }

    return (
      <LineChart
        data={trendsData}
        xAxisKey="label"
        series={[
          {
            dataKey: "resting_heart_rate",
            label: "Resting Heart Rate",
            color: theme.palette.warning.main,
            area: true,
          },
        ]}
        height={300}
        yAxisMin={0}
      />
    );
  };

  const getChipColor = (ragColor: string | undefined): any => {
    switch (ragColor) {
      case "green":
        return "success";
      case "amber":
        return "warning";
      case "red":
        return "error";
      default:
        return "default";
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={3}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, flex: 1 }}>
            <Box sx={{ minWidth: 320 }}>
              <UserSelectSingle
                value={selectedUserId}
                onChange={(id) => setSelectedUserId(id)}
                onUserChange={(u) => setSelectedUser(u)}
                label=""
                placeholder="Select User"
              />
            </Box>
            <DurationSelector
              value={dateRange}
              onChange={handleDateRangeChange}
              buttonVariant="outlined"
            />
            <Button
              variant="text"
              size="medium"
              startIcon={<FilterListIcon />}
              color={activeFiltersCount > 0 ? "primary" : "inherit"}
              onClick={() => setIsFilterOpen(true)}
            >
              {activeFiltersCount > 0 ? `FILTER (${activeFiltersCount})` : "FILTER"}
            </Button>
          </Box>
          <Stack direction="row" spacing={2} alignItems="center">
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={handleViewModeChange}
              size="small"
            >
              <ToggleButton value="user-progress">User Progress</ToggleButton>
              {isAiEnabled ? (
                <ToggleButton value="ai-powered-wellness">
                  AI-Powered Wellness
                </ToggleButton>
              ) : null}
            </ToggleButtonGroup>
            <Box
              component="img"
              src={PeakieWebLogo}
              alt="Peakie AI"
              sx={{ height: 32, width: "auto" }}
            />
          </Stack>
        </Box>

        <FilterDrawer
          open={isFilterOpen}
          onClose={() => setIsFilterOpen(false)}
          onApply={handleApplyFilters}
          initialFilters={filters}
        />

        {selectedUserId ? (
          <Card
            sx={{
              border: "1px solid",
              borderColor: "divider",
              boxShadow: "none",
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 3,
                  flexWrap: { xs: "wrap", md: "nowrap" },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    flexWrap: "wrap",
                    flex: 1,
                  }}
                >
                  <Avatar
                    sx={{
                      width: 64,
                      height: 64,
                      bgcolor: theme.palette.action.disabledBackground,
                    }}
                  >
                    <PersonIcon sx={{ color: theme.palette.text.secondary }} />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight={600}>
                      {userData.name}
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {userData.role ? (
                        <Typography variant="body2" color="text.secondary">
                          {userData.role}
                        </Typography>
                      ) : null}
                      <Chip
                        icon={<LocalFireDepartmentIcon />}
                        label={`${userData.streak}-Day Streak`}
                        size="small"
                      />
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <CalendarTodayIcon fontSize="inherit" />
                        <Typography variant="body2" color="text.secondary">
                          Member since {userData.memberSince}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    p: 2,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <SelfImprovementIcon
                      sx={{
                        color: theme.palette.success.main,
                        fontSize: 40,
                      }}
                    />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "text.secondary",
                      }}
                    >
                      Overall Wellness Score
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "baseline",
                        gap: 0.75,
                        flexWrap: "wrap",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "baseline",
                          gap: 0.5,
                        }}
                      >
                        <Typography
                          variant="h5"
                          component="span"
                          sx={{
                            fontWeight: 700,
                          }}
                        >
                          {headerWellnessScore}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          /100
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 0.25,
                          color: theme.palette.success.main,
                        }}
                      >
                        <Typography
                          component="span"
                          sx={{
                            fontWeight: 600,
                            fontSize: "0.875rem",
                          }}
                        >
                          {userData.wellnessScoreChange}
                        </Typography>
                        <TrendingUpIcon
                          sx={{
                            fontSize: "0.875rem",
                            color: theme.palette.success.main,
                          }}
                        />
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        ) : (
          <Alert severity="info">Select a user to get started.</Alert>
        )}

        {viewMode === "user-progress" && selectedUserId && (
          <Stack spacing={3}>
            {hasError && (
              <Alert severity="error">
                Failed to load user progress data. Please try again later.
              </Alert>
            )}

            <Grid container spacing={3}>
              {isLoading && statCards.length === 0 ? (
                <>
                  {[1, 2, 3, 4, 5, 6, 7, 8].map((index) => (
                    <Grid size={{ xs: 12, sm: 6, md: 3 }} key={index}>
                      <DashboardCardSkeleton />
                    </Grid>
                  ))}
                </>
              ) : (
                statCards.map((stat, index) => (
                  <Grid size={{ xs: 12, sm: 6, md: 3 }} key={index}>
                    <StatCard metric={stat.metric} icon={stat.icon} />
                  </Grid>
                ))
              )}
            </Grid>

            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 4 }}>
                <DashboardCard title="Pace of Aging" subtitle="">
                  {isLoading ? (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: 300,
                      }}
                    >
                      <CircularProgress />
                    </Box>
                  ) : statsQuery.data?.data ? (
                    <>
                      {statsQuery.data.data.pace_of_aging ? (
                        <Box sx={{ mb: 3 }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "baseline",
                              gap: 1,
                              mb: 1,
                            }}
                          >
                            <HourglassEmptyIcon
                              sx={{
                                color: theme.palette.primary.main,
                                fontSize: 28,
                              }}
                            />
                            <Typography
                              variant="h4"
                              sx={{
                                fontWeight: 700,
                                fontSize: "2rem",
                              }}
                            >
                              {statsQuery.data.data.pace_of_aging.value}x
                            </Typography>
                            {(() => {
                              const paceChange =
                                statsQuery.data.data.pace_of_aging.change?.trim() ||
                                "";
                              const isNegative = paceChange.startsWith("-");
                              const isPositive = paceChange.startsWith("+");
                              const paceTrendColor = isNegative
                                ? theme.palette.error.main
                                : isPositive
                                  ? theme.palette.success.main
                                  : theme.palette.text.secondary;
                              const PaceTrendIcon = isNegative
                                ? TrendingDownIcon
                                : isPositive
                                  ? TrendingUpIcon
                                  : TrendingFlatIcon;

                              return (
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.25,
                                    color: paceTrendColor,
                                  }}
                                >
                                  <Typography
                                    component="span"
                                    sx={{
                                      fontWeight: 600,
                                      fontSize: "0.875rem",
                                    }}
                                  >
                                    {paceChange}
                                  </Typography>
                                  <PaceTrendIcon
                                    sx={{
                                      fontSize: "0.875rem",
                                      color: paceTrendColor,
                                    }}
                                  />
                                </Box>
                              );
                            })()}
                          </Box>
                        </Box>
                      ) : (
                        <Alert severity="info" sx={{ mb: 2 }}>
                          Pace of Aging score unavailable
                        </Alert>
                      )}
                      {/* Using the same chips logic as Biometrics or relying on Pace of Aging data if it provides sub-metrics. 
                          For now, using placeholders or available stats if they exist. 
                          Actually, we can use the statsQuery data again here.
                      */}
                      <Stack spacing={2}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                          }}
                        >
                          <AirIcon
                            fontSize="medium"
                            sx={{ color: theme.palette.primary.main }}
                          />
                          <Typography variant="body1" sx={{ flex: 1 }}>
                            Respiration
                          </Typography>
                          <Chip
                            label={
                              statsQuery.data.data.respiratory_rate
                                ?.rag_label || "N/A"
                            }
                            size="small"
                            color={getChipColor(
                              statsQuery.data.data.respiratory_rate?.rag_color
                            )}
                          />
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                          }}
                        >
                          <WaterDropIcon
                            fontSize="medium"
                            sx={{ color: theme.palette.primary.main }}
                          />
                          <Typography variant="body1" sx={{ flex: 1 }}>
                            SpO2
                          </Typography>
                          <Chip
                            label={
                              statsQuery.data.data.spo2?.rag_label || "N/A"
                            }
                            size="small"
                            color={getChipColor(
                              statsQuery.data.data.spo2?.rag_color
                            )}
                          />
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                          }}
                        >
                          <FavoriteIcon
                            fontSize="medium"
                            sx={{ color: theme.palette.primary.main }}
                          />
                          <Typography variant="body1" sx={{ flex: 1 }}>
                            Resting HR
                          </Typography>
                          <Chip
                            label={
                              statsQuery.data.data.resting_heart_rate
                                ?.rag_label || "N/A"
                            }
                            size="small"
                            color={getChipColor(
                              statsQuery.data.data.resting_heart_rate?.rag_color
                            )}
                          />
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                          }}
                        >
                          <MonitorHeartIcon
                            fontSize="medium"
                            sx={{ color: theme.palette.primary.main }}
                          />
                          <Typography variant="body1" sx={{ flex: 1 }}>
                            HRV
                          </Typography>
                          <Chip
                            label={statsQuery.data.data.hrv?.rag_label || "N/A"}
                            size="small"
                            color={getChipColor(
                              statsQuery.data.data.hrv?.rag_color
                            )}
                          />
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                          }}
                        >
                          <ThermostatIcon
                            fontSize="medium"
                            sx={{ color: theme.palette.primary.main }}
                          />
                          <Typography variant="body1" sx={{ flex: 1 }}>
                            Temperature
                          </Typography>
                          <Chip
                            label={
                              statsQuery.data.data.temperature?.rag_label ||
                              "N/A"
                            }
                            size="small"
                            color={getChipColor(
                              statsQuery.data.data.temperature?.rag_color
                            )}
                          />
                        </Box>
                      </Stack>
                    </>
                  ) : (
                    <Alert severity="info">No Pace of Aging data</Alert>
                  )}
                </DashboardCard>
              </Grid>

              <Grid size={{ xs: 12, md: 8 }}>
                <DashboardCard
                  title="Wellbeing Dimensions Overview"
                  subtitle="Health assessment across all key metrics."
                >
                  {isLoading ? (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: 250,
                      }}
                    >
                      <CircularProgress />
                    </Box>
                  ) : wellbeingDimensionsData.length > 0 ? (
                    <Grid container spacing={3}>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <RadarChart
                          data={wellbeingDimensionsData}
                          seriesKeys={["currentScore", "optimalTarget"]}
                          seriesLabels={["Current Score", "Optimal Target"]}
                          metricKey="metric"
                          height={250}
                          colors={[
                            theme.palette.success.main,
                            theme.palette.warning.main,
                          ]}
                          max={100}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <Stack spacing={2}>
                          {wellbeingDimensionsData.map((item, index) => (
                            <Box
                              key={index}
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                gap: 1.5,
                              }}
                            >
                              <AdsClickIcon
                                sx={{
                                  fontSize: 20,
                                  color: theme.palette.text.secondary,
                                  flexShrink: 0,
                                }}
                              />
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ width: "40%", flexShrink: 0 }}
                              >
                                {item.metric}
                              </Typography>
                              <LinearProgress
                                variant="determinate"
                                value={item.currentScore}
                                sx={{
                                  height: 15,
                                  width: "100%",
                                  borderRadius: 1,
                                  bgcolor: theme.palette.action.hover,
                                  "& .MuiLinearProgress-bar": {
                                    borderRadius: 1,
                                    bgcolor: theme.palette.info.light,
                                  },
                                }}
                              />
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ flexShrink: 0 }}
                              >
                                {formatPercentValue(item.currentScore, 2, true)}
                              </Typography>
                            </Box>
                          ))}
                        </Stack>
                      </Grid>
                    </Grid>
                  ) : (
                    <Alert severity="info">
                      No wellbeing dimensions data available
                    </Alert>
                  )}
                </DashboardCard>
              </Grid>
            </Grid>

            <Grid container spacing={3}>
              <Grid size={{ xs: 12 }}>
                <DashboardCard
                  title="Biometric Trends"
                  subtitle="Daily variations across core vital metrics."
                  titleComponent={
                    <ToggleButtonGroup
                      value={biometricTrendView}
                      exclusive
                      onChange={(_, newValue) => {
                        if (newValue !== null) setBiometricTrendView(newValue);
                      }}
                      size="small"
                    >
                      <ToggleButton value="heart-rate">Heart Rate</ToggleButton>
                      <ToggleButton value="heart-rate-variability">
                        Heart Rate Variability
                      </ToggleButton>
                      <ToggleButton value="resting-hr">Resting HR</ToggleButton>
                      <ToggleButton value="respiratory-rate">
                        Respiratory Rate
                      </ToggleButton>
                      <ToggleButton value="spo2">
                        Blood Oxygen (SpO2)
                      </ToggleButton>
                    </ToggleButtonGroup>
                  }
                >
                  {isLoading ? (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: 300,
                      }}
                    >
                      <CircularProgress />
                    </Box>
                  ) : trendsData.length > 0 ? (
                    renderBiometricTrendChart()
                  ) : (
                    <Alert severity="info">No trend data available</Alert>
                  )}
                </DashboardCard>
              </Grid>
            </Grid>

            <Grid container spacing={3}>
              <Grid size={{ xs: 12 }}>
                <DashboardCard
                  title="Sleep Trends"
                  subtitle="Daily patterns of sleep stages showing fluctuations in recovery and rest quality."
                  titleComponent={
                    <ToggleButtonGroup
                      value={sleepTrendView}
                      exclusive
                      onChange={(_, newValue) => {
                        if (newValue !== null) setSleepTrendView(newValue);
                      }}
                      size="small"
                    >
                      <ToggleButton value="sleep-stages">
                        Sleep Stages
                      </ToggleButton>
                      <ToggleButton value="efficiency">Efficiency</ToggleButton>
                      <ToggleButton value="sleep-debt">Sleep Debt</ToggleButton>
                      <ToggleButton value="sleep-stress">
                        Sleep Stress
                      </ToggleButton>
                    </ToggleButtonGroup>
                  }
                >
                  {isLoading ? (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: 300,
                      }}
                    >
                      <CircularProgress />
                    </Box>
                  ) : trendsData.length > 0 ? (
                    renderSleepTrendChart()
                  ) : (
                    <Alert severity="info">No trend data available</Alert>
                  )}
                </DashboardCard>
              </Grid>
            </Grid>

            <Grid container spacing={3}>
              <Grid size={{ xs: 12 }}>
                <DashboardCard
                  title="Recovery Trends"
                  subtitle="Daily trends showing recovery and strain percentages over time."
                  titleComponent={
                    <ToggleButtonGroup
                      value={recoveryTrendView}
                      exclusive
                      onChange={(_, newValue) => {
                        if (newValue !== null) setRecoveryTrendView(newValue);
                      }}
                      size="small"
                    >
                      <ToggleButton value="recovery-score">
                        Recovery Score
                      </ToggleButton>
                      <ToggleButton value="resting-heart-rate">
                        Resting Heart Rate
                      </ToggleButton>
                    </ToggleButtonGroup>
                  }
                >
                  {isLoading ? (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: 300,
                      }}
                    >
                      <CircularProgress />
                    </Box>
                  ) : trendsData.length > 0 ? (
                    renderRecoveryTrendChart()
                  ) : (
                    <Alert severity="info">No trend data available</Alert>
                  )}
                </DashboardCard>
              </Grid>
            </Grid>
          </Stack>
        )}

        {viewMode === "ai-powered-wellness" && isAiEnabled && (
          <Stack spacing={3}>
            {!insightsUserId && (
              <Alert severity="info">
                Select a user to view AI-powered insights.
              </Alert>
            )}

            {standardInsightsError && (
              <Alert severity="error">
                Failed to load insights.{" "}
                {standardInsightsError instanceof Error
                  ? standardInsightsError.message
                  : "Please try again."}
              </Alert>
            )}

            {standardInsightsLoading && insightsUserId && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                <CircularProgress />
              </Box>
            )}

            {standardInsights && insightsUserId && (
              <>
                <Card
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    boxShadow: "none",
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 3,
                        flexWrap: { xs: "wrap", md: "nowrap" },
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <CircularProgressIndicator
                          value={standardInsights.overall_wellness_score.value}
                          label=""
                          icon={null}
                          color={theme.palette.success.main}
                          size={120}
                          max={100}
                        />
                      </Box>

                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="h6" sx={{ mb: 1 }}>
                          Overall Score Summary
                        </Typography>

                        {overallScoreSummarySection?.text ? (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ lineHeight: 1.6 }}
                          >
                            {overallScoreSummarySection.text}
                          </Typography>
                        ) : (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ lineHeight: 1.6 }}
                          >
                            {standardInsights.overall_wellness_score
                              .calculation_summary_text ||
                              standardInsights.overall_wellness_score
                                .calculation_summary}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </CardContent>
                </Card>

                <Alert severity="warning" icon={<WarningAmberIcon />}>
                  <Typography variant="body2">
                    This is not medical advice and this narrative is generated
                    using AI analysis of users&apos; wearable data patterns.
                    Please check for accuracy.
                  </Typography>
                </Alert>

                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <DashboardCard>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 2,
                        }}
                      >
                        <LightbulbIcon
                          sx={{
                            color: theme.palette.success.main,
                            fontSize: 20,
                          }}
                        />
                        <Typography
                          variant="body1"
                          fontWeight={600}
                          color="success.main"
                        >
                          Key Highlights
                        </Typography>
                      </Box>
                      {keyHighlightsSection?.items?.length ? (
                        <Stack spacing={2}>
                          {keyHighlightsSection.items
                            .slice(0, 12)
                            .map((item, idx) => (
                              <Box
                                key={`kh-${idx}`}
                                sx={{
                                  display: "flex",
                                  gap: 1.5,
                                  alignItems: "flex-start",
                                }}
                              >
                                <Box
                                  sx={{
                                    width: 6,
                                    height: 6,
                                    borderRadius: "50%",
                                    bgcolor: theme.palette.success.main,
                                    mt: 1,
                                    flexShrink: 0,
                                  }}
                                />
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  {item}
                                </Typography>
                              </Box>
                            ))}
                          {keyHighlightsSection.text ? (
                            <Typography variant="body2" color="text.secondary">
                              {keyHighlightsSection.text}
                            </Typography>
                          ) : null}
                        </Stack>
                      ) : standardInsights.key_highlights?.length ? (
                        <Stack spacing={2}>
                          {standardInsights.key_highlights
                            .slice(0, 6)
                            .map((h, idx) => (
                              <Box
                                key={`${h.metric_id}-${idx}`}
                                sx={{
                                  display: "flex",
                                  gap: 1.5,
                                  alignItems: "flex-start",
                                }}
                              >
                                <Box
                                  sx={{
                                    width: 6,
                                    height: 6,
                                    borderRadius: "50%",
                                    bgcolor: theme.palette.success.main,
                                    mt: 1,
                                    flexShrink: 0,
                                  }}
                                />
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  {h.insight ||
                                    `${h.title}: ${h.value}${
                                      h.unit ? ` ${h.unit}` : ""
                                    }`}
                                </Typography>
                              </Box>
                            ))}
                        </Stack>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No highlights available.
                        </Typography>
                      )}
                    </DashboardCard>
                  </Grid>

                  <Grid size={{ xs: 12, md: 4 }}>
                    <DashboardCard>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 2,
                        }}
                      >
                        <PersonIcon
                          sx={{
                            color: theme.palette.primary.main,
                            fontSize: 20,
                          }}
                        />
                        <Typography
                          variant="body1"
                          fontWeight={600}
                          color="primary.main"
                        >
                          Personalized Recommendations
                        </Typography>
                      </Box>
                      {recommendationsSection?.items?.length ? (
                        <Stack spacing={2}>
                          {recommendationsSection.items
                            .slice(0, 18)
                            .map((item, idx) => {
                              const isHeading = item.trim().endsWith(":");
                              return (
                                <Box
                                  key={`rec-${idx}`}
                                  sx={{
                                    display: "flex",
                                    gap: 1.5,
                                    alignItems: "flex-start",
                                  }}
                                >
                                  <Box
                                    sx={{
                                      width: 6,
                                      height: 6,
                                      borderRadius: "50%",
                                      bgcolor: theme.palette.primary.main,
                                      mt: 1,
                                      flexShrink: 0,
                                      opacity: isHeading ? 0.6 : 1,
                                    }}
                                  />
                                  <Typography
                                    variant="body2"
                                    color={
                                      isHeading
                                        ? "text.primary"
                                        : "text.secondary"
                                    }
                                    fontWeight={isHeading ? 700 : 400}
                                  >
                                    {item}
                                  </Typography>
                                </Box>
                              );
                            })}
                          {recommendationsSection.text ? (
                            <Typography variant="body2" color="text.secondary">
                              {recommendationsSection.text}
                            </Typography>
                          ) : null}
                        </Stack>
                      ) : standardInsights.personalised_recommendations
                          ?.length ? (
                        <Stack spacing={2}>
                          {standardInsights.personalised_recommendations
                            .slice(0, 6)
                            .map((r, idx) => (
                              <Box
                                key={`${r.title}-${idx}`}
                                sx={{
                                  display: "flex",
                                  gap: 1.5,
                                  alignItems: "flex-start",
                                }}
                              >
                                <Box
                                  sx={{
                                    width: 6,
                                    height: 6,
                                    borderRadius: "50%",
                                    bgcolor: theme.palette.primary.main,
                                    mt: 1,
                                    flexShrink: 0,
                                  }}
                                />
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  {r.title}
                                </Typography>
                              </Box>
                            ))}
                        </Stack>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No recommendations available.
                        </Typography>
                      )}
                    </DashboardCard>
                  </Grid>

                  <Grid size={{ xs: 12, md: 4 }}>
                    <DashboardCard>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 2,
                        }}
                      >
                        <WarningAmberIcon
                          sx={{
                            color: theme.palette.warning.main,
                            fontSize: 20,
                          }}
                        />
                        <Typography
                          variant="body1"
                          fontWeight={600}
                          color="warning.main"
                        >
                          Areas to Watch
                        </Typography>
                      </Box>
                      {areasToWatchSection?.items?.length ? (
                        <Stack spacing={2}>
                          {areasToWatchSection.items
                            .slice(0, 12)
                            .map((item, idx) => (
                              <Box
                                key={`watch-${idx}`}
                                sx={{
                                  display: "flex",
                                  gap: 1.5,
                                  alignItems: "flex-start",
                                }}
                              >
                                <Box
                                  sx={{
                                    width: 6,
                                    height: 6,
                                    borderRadius: "50%",
                                    bgcolor: theme.palette.warning.main,
                                    mt: 1,
                                    flexShrink: 0,
                                  }}
                                />
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  {item}
                                </Typography>
                              </Box>
                            ))}
                          {areasToWatchSection.text ? (
                            <Typography variant="body2" color="text.secondary">
                              {areasToWatchSection.text}
                            </Typography>
                          ) : null}
                        </Stack>
                      ) : standardInsights.areas_to_watch?.length ? (
                        <Stack spacing={2}>
                          {standardInsights.areas_to_watch
                            .slice(0, 6)
                            .map((a, idx) => (
                              <Box
                                key={`${a.metric_id}-${idx}`}
                                sx={{
                                  display: "flex",
                                  gap: 1.5,
                                  alignItems: "flex-start",
                                }}
                              >
                                <Box
                                  sx={{
                                    width: 6,
                                    height: 6,
                                    borderRadius: "50%",
                                    bgcolor: theme.palette.warning.main,
                                    mt: 1,
                                    flexShrink: 0,
                                  }}
                                />
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  {a.reason}
                                </Typography>
                              </Box>
                            ))}
                        </Stack>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No watch areas available.
                        </Typography>
                      )}
                    </DashboardCard>
                  </Grid>
                </Grid>
              </>
            )}
          </Stack>
        )}
      </Stack>
    </Box>
  );
};
